/**
 * Seção de Ações Rápidas (Regras de Roteamento Manual)
 * 
 * Exibe regras com condition_type='manual_execution' como botões
 * que o agente pode executar durante uma conversa.
 * Inclui ícones de visualização (preview), edição e criação.
 * 
 * CHANGELOG:
 *   2026-06-23 v1.3.0 - Agrupamento por categoria (espelha QuickActionsSheet):
 *                      cabeçalhos colapsáveis por categoria com persistência
 *                      em localStorage. Estende ManualRule com colunas de
 *                      categoria já retornadas pela RPC get_manual_routing_rules.
 *   2025-12-15 v1.2.0 - Versão anterior.
 *
 * @version 1.3.0
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Zap,
  Play,
  Loader2,
  Plus,
  ChevronRight,
  ChevronDown,
  Eye,
  Pencil,
  Users,
  Globe,
  FolderTree,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { RulePreviewDialog } from '@/components/tenant/routing/RulePreviewDialog';
import { EditRuleDialog } from '@/components/tenant/routing/EditRuleDialog';
import { CreateManualRuleDialog } from '@/components/tenant/routing/CreateManualRuleDialog';
import { RoutingCategoryIcon } from '@/components/tenant/routing/categories/RoutingCategoryIcon';
import { RoutingRule } from '@/pages/tenant/RoutingRules';

// Logger para o módulo
const logger = createLogger('ManualRoutingSection');

// Interface para regra manual retornada pela RPC (inclui colunas de categoria v1.1.0)
interface ManualRule {
  id: string;
  name: string;
  description: string | null;
  created_by: string | null;
  visibility: string;
  is_owner: boolean;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  category_icon: string | null;
  category_sort_order: number | null;
}


// Props do componente
interface ManualRoutingSectionProps {
  /** ID da inbox atual */
  inboxId: string;
  /** ID da conversa atual */
  conversationId: string;
  /** ID do contato atual */
  contactId: string;
  /** Telefone do contato (para variáveis) */
  contactPhone: string;
  /** Nome do contato (para variáveis) */
  contactName?: string | null;
  /** ID do tenant */
  tenantId: string;
  /** Contagem inicial vinda da RPC de contadores (exibe antes de expandir) */
  initialCount?: number;
}

/**
 * Componente de seção sanfonada com ações rápidas
 * Lazy loading: só carrega regras quando aberto
 */
export function ManualRoutingSection({
  inboxId,
  conversationId,
  contactId,
  contactPhone,
  contactName,
  tenantId,
  initialCount,
}: ManualRoutingSectionProps) {
  // Obtém usuário logado para filtrar regras por criador
  const { user } = useAuth();
  // Estados
  const [isOpen, setIsOpen] = useState(false);
  const [executingRuleId, setExecutingRuleId] = useState<string | null>(null);
  const [previewRule, setPreviewRule] = useState<ManualRule | null>(null);
  const [editingRule, setEditingRule] = useState<RoutingRule | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Query para buscar regras manuais via RPC (lazy loading)
  const { data: manualRules, isLoading, refetch } = useQuery({
    queryKey: ['manual-routing-rules', tenantId, inboxId],
    queryFn: async () => {
      logger.debug('Buscando regras manuais via RPC', { tenantId, inboxId });

      // Usa RPC que considera visibilidade (private/shared/public)
      const { data, error } = await supabase.rpc('get_manual_routing_rules', {
        p_inbox_id: inboxId,
      });

      if (error) throw error;

      logger.debug('Regras manuais encontradas', { count: (data || []).length });
      return (data || []) as ManualRule[];
    },
    enabled: isOpen,
    staleTime: 30000,
  });

  /**
   * Handler para executar uma regra manual
   * Chama a Edge Function routing-engine com action: 'execute_manual'
   */
  const handleExecuteRule = async (rule: ManualRule) => {
    setExecutingRuleId(rule.id);
    logger.info('Executando regra manual', { ruleId: rule.id, ruleName: rule.name });

    try {
      const { data, error } = await supabase.functions.invoke('routing-engine', {
        body: {
          action: 'execute_manual',
          rule_id: rule.id,
          tenant_id: tenantId,
          inbox_id: inboxId,
          conversation_id: conversationId,
          contact_id: contactId,
          contact_phone: contactPhone,
          contact_name: contactName || undefined,
        },
      });

      if (error) {
        logger.error('Erro na Edge Function', error);
        throw error;
      }

      if (data?.success) {
        showToast.success(
          `Regra "${rule.name}" executada!`,
          data.messages_sent > 0 
            ? `${data.messages_sent} mensagem(s) enviada(s)` 
            : 'Ações aplicadas com sucesso'
        );
        logger.info('Regra executada com sucesso', { ruleId: rule.id, result: data });
      } else {
        throw new Error(data?.error || 'Erro desconhecido');
      }
    } catch (error) {
      logger.error('Erro ao executar regra', error);
      showToast.error(
        'Erro ao executar regra',
        error instanceof Error ? error.message : 'Tente novamente'
      );
    } finally {
      setExecutingRuleId(null);
    }
  };

  /**
   * Handler para abrir modal de edição
   * Busca dados completos da regra
   */
  const handleOpenEdit = async (ruleId: string) => {
    logger.debug('Abrindo edição da regra', { ruleId });

    try {
      // Carrega regra base + ações em paralelo para hidratar o payload no
      // mesmo formato que o EditRuleDialog espera (compatível com a página
      // de Configurações que usa get_tenant_routing_rules_full)
      const [ruleRes, actionsRes] = await Promise.all([
        supabase.from('routing_rules').select('*').eq('id', ruleId).single(),
        supabase
          .from('routing_rule_actions')
          .select('id, action_type, action_value, sort_order, delay_seconds')
          .eq('rule_id', ruleId)
          .order('sort_order', { ascending: true }),
      ]);

      if (ruleRes.error) throw ruleRes.error;
      if (actionsRes.error) throw actionsRes.error;

      if (ruleRes.data) {
        setEditingRule({
          ...(ruleRes.data as Record<string, unknown>),
          actions: actionsRes.data || [],
        } as unknown as RoutingRule);
      }
    } catch (error) {
      logger.error('Erro ao carregar regra para edição', error);
      showToast.error('Erro ao carregar regra');
    }
  };

  /**
   * Callback após edição bem-sucedida
   */
  const handleEditSuccess = () => {
    setEditingRule(null);
    refetch(); // Atualiza lista de regras
  };

  /**
   * Callback após criação bem-sucedida
   */
  const handleCreateSuccess = () => {
    setShowCreateDialog(false);
    refetch(); // Atualiza lista de regras
  };

  // ---------------------------------------------------------------
  // Agrupamento por categoria (v1.3.0 - espelha QuickActionsSheet)
  // ---------------------------------------------------------------
  interface CategoryGroup {
    key: string;
    name: string;
    color: string | null;
    icon: string | null;
    sortOrder: number;
    rules: ManualRule[];
  }

  // Constrói grupos preservando a ordem das categorias e mantendo
  // "Sem categoria" sempre no final.
  const groupedRules = useMemo<CategoryGroup[]>(() => {
    const list = manualRules ?? [];
    const map = new Map<string, CategoryGroup>();
    for (const r of list) {
      const key = r.category_id ?? '__none__';
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: r.category_name ?? 'Sem categoria',
          color: r.category_color,
          icon: r.category_icon,
          sortOrder: r.category_sort_order ?? Number.MAX_SAFE_INTEGER - 1,
          rules: [],
        });
      }
      map.get(key)!.rules.push(r);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [manualRules]);

  // Persistência do colapso por categoria em localStorage
  const COLLAPSE_STORAGE_KEY = 'manual-routing-collapsed-categories';
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleCollapsed = (key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(Array.from(next)));
      } catch {
        // ignora falha de storage
      }
      return next;
    });
  };

  return (

    <>
      {/* Card Premium para Ações Rápidas */}
      <div className="bg-card rounded-xl border shadow-sm overflow-clip">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-4 hover:bg-muted/30 transition-colors">
            <span className="text-sm font-medium text-foreground">
              Ações Rápidas
            </span>
            <div className="flex items-center gap-2">
              {/* Badge com contagem: usa dados carregados se aberto, senão initialCount */}
              {(manualRules ? manualRules.length : (initialCount ?? 0)) > 0 && (
                <Badge variant="secondary" className="rounded-full text-xs">
                  {manualRules ? manualRules.length : initialCount}
                </Badge>
              )}
              <ChevronRight className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                isOpen && "rotate-90"
              )} />
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="px-4 pb-4 space-y-2">
            {/* Botão criar ação rápida */}
            <div className="flex justify-end mb-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                    onClick={() => setShowCreateDialog(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Nova ação
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Criar ação rápida</TooltipContent>
              </Tooltip>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : manualRules && manualRules.length > 0 ? (
              <div className="space-y-3">
                {groupedRules.map((group) => {
                  const isCollapsed = collapsedKeys.has(group.key);
                  return (
                    <div key={group.key} className="space-y-1.5">
                      {/* Cabeçalho da categoria */}
                      <button
                        type="button"
                        onClick={() => toggleCollapsed(group.key)}
                        className="flex items-center gap-1.5 w-full text-left px-1 py-1 rounded hover:bg-muted/40 transition-colors"
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                        )}
                        {/* Ícone colorido da categoria (ou pasta neutra) */}
                        <div
                          className="h-4 w-4 rounded flex items-center justify-center shrink-0"
                          style={{
                            backgroundColor: group.color ? `${group.color}1a` : undefined,
                          }}
                        >
                          {group.icon ? (
                            <RoutingCategoryIcon
                              name={group.icon}
                              color={group.color || undefined}
                              className="h-3 w-3"
                            />
                          ) : (
                            <FolderTree className="h-3 w-3 text-muted-foreground" />
                          )}
                        </div>
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground truncate flex-1">
                          {group.name}
                        </span>
                        <Badge variant="secondary" className="rounded-full text-[10px] h-4 px-1.5 shrink-0">
                          {group.rules.length}
                        </Badge>
                      </button>

                      {/* Regras do grupo */}
                      {!isCollapsed && (
                        <div className="space-y-2">
                          {group.rules.map((rule) => (
                            <div
                              key={rule.id}
                              className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-1 border rounded-lg px-2 py-1.5 bg-muted/30"
                            >
                              {/* Botão Play (executa a regra) */}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="min-w-0 justify-start gap-2 h-auto py-1 px-2 overflow-hidden"
                                disabled={executingRuleId === rule.id}
                                onClick={() => handleExecuteRule(rule)}
                                title={rule.description || undefined}
                              >
                                {executingRuleId === rule.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                                ) : (
                                  <Play className="h-4 w-4 shrink-0 text-amber-500" />
                                )}
                                <span className="truncate text-sm">{rule.name}</span>
                                {!rule.is_owner && rule.visibility === 'shared' && (
                                  <Users className="h-3 w-3 shrink-0 text-muted-foreground" />
                                )}
                                {!rule.is_owner && rule.visibility === 'public' && (
                                  <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
                                )}
                              </Button>

                              {/* Ícone Olho (preview) */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0"
                                    onClick={() => setPreviewRule(rule)}
                                  >
                                    <Eye className="h-4 w-4 text-muted-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Visualizar ações</TooltipContent>
                              </Tooltip>

                              {/* Ícone Editar (apenas para o criador) */}
                              {rule.is_owner ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 shrink-0"
                                      onClick={() => handleOpenEdit(rule.id)}
                                    >
                                      <Pencil className="h-4 w-4 text-muted-foreground" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Editar regra</TooltipContent>
                                </Tooltip>
                              ) : (
                                <div className="h-7 w-7" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            ) : (
              <p className="text-xs text-muted-foreground text-center py-3">
                Nenhuma ação rápida criada.
                <br />
                <Button
                  variant="link"
                  size="sm"
                  className="text-amber-500 p-0 h-auto text-xs"
                  onClick={() => setShowCreateDialog(true)}
                >
                  Clique aqui para criar uma
                </Button>
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Dialog de Preview */}
      {previewRule && (
        <RulePreviewDialog
          open={!!previewRule}
          onOpenChange={(open) => !open && setPreviewRule(null)}
          ruleId={previewRule.id}
          ruleName={previewRule.name}
          ruleDescription={previewRule.description}
        />
      )}

      {/* Dialog de Edição */}
      {editingRule && (
        <EditRuleDialog
          open={!!editingRule}
          onOpenChange={(open) => !open && setEditingRule(null)}
          rule={editingRule}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Dialog de Criação de Ação Rápida */}
      <CreateManualRuleDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
}
